import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader } from "../../components/common/PageHeader";
import {
  useListUsers,
  useAdminListOrganizations,
  getListUsersQueryKey,
  approveUser as approveUserApi,
  rejectUser as rejectUserApi,
} from "../../api/generated/generated";
import type { AdminUserSummary, Role } from "../../api/generated";

type StatusTab = "all" | "pending" | "active" | "rejected";

const STATUS_CHIP_COLORS: Record<
  string,
  "warning" | "success" | "error" | "default"
> = {
  pending: "warning",
  active: "success",
  rejected: "error",
};

export function AdminUsersPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [emailFilter, setEmailFilter] = useState("");
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Approve dialog state
  const [approveTarget, setApproveTarget] = useState<AdminUserSummary | null>(
    null,
  );
  const [approveOrgId, setApproveOrgId] = useState("");
  const [approveRole, setApproveRole] = useState("user");
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState("");

  // Reject dialog state
  const [rejectTarget, setRejectTarget] = useState<AdminUserSummary | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState("");

  const statusFilter = statusTab === "all" ? undefined : statusTab;

  const {
    data: usersResp,
    isLoading,
    error,
  } = useListUsers(
    {
      email: emailFilter || undefined,
      status: statusFilter,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
    { query: { staleTime: 30_000 } },
  );
  const data = usersResp?.status === 200 ? usersResp.data : undefined;

  // Pending count for badge
  const { data: pendingResp } = useListUsers(
    { status: "pending", limit: 1, offset: 0 },
    { query: { staleTime: 30_000 } },
  );
  const pendingCount = pendingResp?.status === 200 ? pendingResp.data.total : 0;

  // Orgs for approve dialog
  const { data: orgsResp } = useAdminListOrganizations(
    { limit: 200, offset: 0 },
    { query: { enabled: !!approveTarget, staleTime: 60_000 } },
  );
  const orgs = orgsResp?.status === 200 ? orgsResp.data.organizations : [];

  const invalidateUsers = () => {
    queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });
  };

  const handleApprove = async () => {
    if (!approveTarget || !approveOrgId) return;
    setApproveLoading(true);
    setApproveError("");
    try {
      await approveUserApi(approveTarget.id, {
        org_id: approveOrgId,
        role: approveRole as Role,
      });
      setApproveTarget(null);
      setApproveOrgId("");
      setApproveRole("user");
      invalidateUsers();
    } catch (err: any) {
      setApproveError(err?.message || "Failed to approve user");
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setRejectLoading(true);
    setRejectError("");
    try {
      await rejectUserApi(rejectTarget.id, { reason: rejectReason || null });
      setRejectTarget(null);
      setRejectReason("");
      invalidateUsers();
    } catch (err: any) {
      setRejectError(err?.message || "Failed to reject user");
    } finally {
      setRejectLoading(false);
    }
  };

  if (!user?.superAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        You do not have permission to access this page.
      </Alert>
    );
  }

  const getUserStatus = (u: AdminUserSummary): string => {
    return u.status ?? "active";
  };

  return (
    <Box>
      <PageHeader title="Users" />
      <Tabs
        value={statusTab}
        onChange={(_, v) => {
          setStatusTab(v);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="All" value="all" />
        <Tab
          label={
            <Badge badgeContent={pendingCount} color="warning" max={99}>
              <Box sx={{ px: 1 }}>Pending</Box>
            </Badge>
          }
          value="pending"
        />
        <Tab label="Active" value="active" />
        <Tab label="Rejected" value="rejected" />
      </Tabs>
      <TextField
        placeholder="Search by email..."
        size="small"
        value={emailFilter}
        onChange={(e) => {
          setEmailFilter(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2, width: 300 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users.
        </Alert>
      )}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.users.map((u) => {
                  const status = getUserStatus(u);
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {u.first_name} {u.last_name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status}
                          size="small"
                          color={STATUS_CHIP_COLORS[status] ?? "default"}
                        />
                      </TableCell>
                      <TableCell>{u.org_id ?? "-"}</TableCell>
                      <TableCell>
                        {u.role ? <Chip label={u.role} size="small" /> : "-"}
                      </TableCell>
                      <TableCell>
                        {u.super_admin && (
                          <Chip
                            label="Super Admin"
                            size="small"
                            color="error"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {status === "pending" && (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Button
                              size="small"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => setApproveTarget(u)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => setRejectTarget(u)}
                            >
                              Reject
                            </Button>
                          </Box>
                        )}
                        {status === "rejected" && (
                          <Button
                            size="small"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => setApproveTarget(u)}
                          >
                            Approve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data?.users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography
                        sx={{
                          color: "text.secondary",
                          py: 2
                        }}>
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
      {/* Approve Dialog */}
      <Dialog
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve User: {approveTarget?.email}</DialogTitle>
        <DialogContent>
          {approveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {approveError}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={approveOrgId}
              label="Organization"
              onChange={(e) => setApproveOrgId(e.target.value)}
            >
              {orgs.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={approveRole}
              label="Role"
              onChange={(e) => setApproveRole(e.target.value)}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="sales_manager">Sales Manager</MenuItem>
              <MenuItem value="org_owner">Org Owner</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)}>Cancel</Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={!approveOrgId || approveLoading}
          >
            {approveLoading ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reject Dialog */}
      <Dialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject User: {rejectTarget?.email}</DialogTitle>
        <DialogContent>
          {rejectError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rejectError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={rejectLoading}
          >
            {rejectLoading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
